<script lang="ts" setup>
// TableauValeursEnedis (DEV-1508 lot 2, E2) — le dépliant « Valeurs » sous un
// graphe : les mêmes points, en chiffres, au grain qui tient à l'écran.
//
// Ce que ce composant tient :
//  • il est FERMÉ tant qu'on ne l'ouvre pas, et il ne calcule rien avant ;
//  • son grain est celui que la règle des 750 lignes décide, écrit dans son
//    titre. Quand il est plus grossier que le graphe, il donne le GESTE pour
//    descendre plutôt que de s'excuser ;
//  • une valeur absente laisse une CELLULE VIDE. Jamais un 0, jamais un tiret
//    qui se lirait comme une mesure à zéro (décision de Mathieu du 07/09) ;
//  • un TOTAL sur une fenêtre trouée dit combien de pas lui manquent (item 32
//    du brief de design v1.1) : un total plus bas ne doit pas se lire comme une
//    consommation plus faible ;
//  • ce qui manque à la série tout entière, parce que la fenêtre dépasse celle
//    de son calcul, est écrit en tête du dépliant : le graphe le dit, les
//    chiffres le disent aussi, personne ne cherche une colonne absente.
//
// UN SEUL DÉPLIANT PAR GRAPHE (lot B2). Le composant recevait UNE série et la
// vue en posait autant d'exemplaires qu'il y avait de courbes : sept boutons
// « Valeurs … » alignés sous le graphe Puissance d'un C2, six de plus que ce
// que quiconque ouvre. Il reçoit désormais TOUTES les séries tracées du graphe
// et n'en montre qu'une à la fois, choisie dans un sélecteur qui n'apparaît
// que s'il y a quelque chose à choisir. Changer de série garde la fenêtre et
// le grain, et ramène la pagination à sa première page : on regarde la même
// période, sur une autre courbe.
//
// Ce n'est pas un export : qui veut ses milliers de lignes les prend par le
// bouton Exporter, inchangé (E10).
//
// MISE EN FORME (lot 4, items 19, 20, 31, 33, 39). Le tableau maison laisse la
// place au `BaseDataTable` paginé de l'application, la bascule à un
// `BaseButton text`, et le titre du dépliant devient un `h4` sous le `h2` de la
// carte du graphe. Deux classes survivent comme SÉLECTEURS de test et non comme
// styles : `.valeurs__bascule-serie` et `.valeurs__num`.
//
// Ce que le paginateur PrimeVue ne fait PAS : il ne rend aucun libellé visible
// « Lignes par page ». Vérifié, son `RowsPerPageDropdown` ne reçoit qu'un
// `aria-label`. Le libellé visible est donc posé à la main dans le slot
// `#paginatorstart`, et la version lue par une aide technique vient de la
// locale française posée dans `primevue.ts` (item 30).
import { computed, ref, watch } from "vue"
import {
    CHOIX_LIGNES_PAR_PAGE,
    LIGNES_PAR_PAGE,
    cleColonneAlignee,
    colonnesDuTableau,
    colonnesDuMiroir,
    formateNombre,
    grainQuiTient,
    lignesDuTableau,
    nombreDeLignes,
    pageDeLaDate,
    phraseDuGrain,
    texteDesPoints,
    texteDuTotal,
    titreDuDepliant,
    MENTION_PARTIEL,
    type ColonneValeurs,
    type GrainTableau,
    type LigneValeurs,
} from "@modules/data/utils/tableauValeurs"
import type { SerieTracee } from "@modules/data/utils/graphesEnedis"
import { LIBELLES_ORIGINE } from "@modules/data/utils/ongletsEnedis"
import {
    ETATS_COMPLETUDE,
    type EtatCompletude,
    type JourComplet,
} from "@modules/data/utils/completude"
import DataOrigineBadge from "./DataOrigineBadge.vue"

const props = withDefaults(
    defineProps<{
        /**
         * Les séries TRACÉES du graphe, dans l'ordre du graphe. Le dépliant en
         * montre une à la fois ; la première est celle qu'il ouvre.
         */
        series: SerieTracee[]
        /** Grain décidé par la règle des lignes. */
        grain: GrainTableau
        /** Grain réellement servi par le lecteur, pour dire l'écart. */
        grainServi: GrainTableau
        /** Vrai pour une grandeur qui s'additionne. */
        sommable?: boolean
        unite?: string | null
        /**
         * Ce qui n'est pas servi sur cette fenêtre, dit plutôt que tu, POUR
         * CHAQUE série : la mention suit la série qu'on regarde, sinon elle
         * parlerait d'une autre courbe que celle qui est sous les yeux.
         */
        mentionsParSerie?: Record<string, string[]>
        /**
         * LES JOURS DE LA BANDE DE COMPLÉTUDE (V3), tels qu'elle les a calculés.
         *
         * La colonne « Qualité » ne recalcule rien : elle LIT l'état que la
         * bande affiche déjà, à la date de la ligne. Deux calculs du même fait
         * finiraient par se contredire sur le même écran. Vide = la bande n'a
         * pas encore son verdict : pas de colonne plutôt qu'un mot démenti une
         * seconde plus tard.
         */
        joursDuContexte?: JourComplet[]
        /**
         * `meta.at` par série, indexé comme le lecteur l'indexe
         * (`direction|variable.stat`, cf. `cleColonneAlignee`) : l'heure
         * d'atteinte de chaque point, alignée sur son `x`. Servie pour la PMax,
         * absente partout ailleurs (V6).
         */
        heuresAtteinteParSerie?: Record<string, Array<string | null>>
        /** Le pas du compteur, pour écrire « au pas de 30 min » dans le titre. */
        minutesPasNatif?: number | null
        /**
         * LE TABLEAU EN MIROIR (lot 5g, `tableMiroir` de la maquette).
         *
         * Quand la figure porte les deux sens, le tableau les met CÔTE À CÔTE :
         * Date · Soutirée · Qualité · Injectée · Qualité · Origine. Une seule
         * ligne par date, deux valeurs, deux qualités — et jamais une
         * soustraction : ce sont deux colonnes, pas un solde (E5).
         *
         * Faux par défaut : tout le reste de la page garde son dépliant à une
         * série, celui qu'il a toujours eu.
         */
        miroir?: boolean
        /**
         * Les jours de la bande de l'INJECTION (V3, lot 5g). La qualité de la
         * colonne d'injection s'y lit ; sans eux, elle recopierait celle du
         * soutirage, qui ne dit rien de l'autre sens.
         */
        joursDuContexteInjection?: JourComplet[]
    }>(),
    {
        sommable: false,
        unite: null,
        mentionsParSerie: () => ({}),
        joursDuContexte: () => [],
        heuresAtteinteParSerie: () => ({}),
        minutesPasNatif: null,
        miroir: false,
        joursDuContexteInjection: () => [],
    }
)

const emit = defineEmits<{
    /**
     * Le dépliant s'ouvre : le suivi veut le grain, le nombre de lignes et la
     * série regardée.
     */
    (
        e: "deplie",
        detail: { grain: GrainTableau; lignes: number; serie: string }
    ): void
}>()

const ouvert = ref(false)

/**
 * LE MIROIR EST ARMÉ quand la figure le demande ET que les deux sens portent la
 * MÊME grandeur, chacun une fois. Sur toute autre forme (trois séries, deux
 * grandeurs, un seul sens), le dépliant reste celui qu'il a toujours été : une
 * série à la fois, choisie dans le sélecteur.
 */
const paireMiroir = computed<{
    soutirage: SerieTracee
    injection: SerieTracee
} | null>(() => {
    if (!props.miroir) return null
    const soutirage = props.series.filter((s) => s.direction === "soutirage")
    const injection = props.series.filter((s) => s.direction === "injection")
    if (soutirage.length !== 1 || injection.length !== 1) return null
    if (soutirage[0].libelleBase !== injection[0].libelleBase) return null
    return { soutirage: soutirage[0], injection: injection[0] }
})

/** La série regardée. Par défaut la première, c'est-à-dire celle du graphe. */
const cleChoisie = ref(props.series[0]?.cle ?? "")
/** Le sélecteur ne s'affiche que s'il y a un choix à faire, jamais en miroir. */
const plusieurs = computed(
    () => props.series.length > 1 && paireMiroir.value === null
)
const serie = computed(() =>
    paireMiroir.value
        ? paireMiroir.value.soutirage
        : (props.series.find((s) => s.cle === cleChoisie.value) ??
          props.series[0])
)
const choixDeSerie = computed(() =>
    props.series.map((s) => ({ name: s.libelle, value: s.cle }))
)
/** Un identifiant stable pour lier le libellé au sélecteur, carte par carte. */
const idDuSelecteur = computed(
    () => `serie-${props.series[0]?.cle ?? "valeurs"}`
)
/** Ce que la série regardée n'a pas sur cette fenêtre. */
const mentions = computed(
    () => props.mentionsParSerie[serie.value?.cle ?? ""] ?? []
)
/** Index de la PREMIÈRE ligne de la page, le compte que le paginateur tient. */
const premiere = ref(0)
const parPage = ref(LIGNES_PAR_PAGE)
const dateCherchee = ref("")
const dateIntrouvable = ref(false)

/**
 * LE GRAIN RÉEL DU TABLEAU (V1, §7.5 du plan).
 *
 * `grain` est décidé par la vue sur la fenêtre REGARDÉE ; le dépliant, lui,
 * chiffre tout le contexte chargé. On remonte donc d'un cran tant que le compte
 * réel des lignes dépasse le plafond, et c'est CE grain-là que le titre annonce.
 * Un titre qui promet « au jour » devant 8 760 lignes ment sur les deux.
 */
const grainReel = computed<GrainTableau>(() =>
    serie.value ? grainQuiTient(serie.value.x, props.grain) : props.grain
)

/**
 * Série servie au grain PÉRIODE : ses points sont des couples [début, fin]
 * (V6). Le fait se LIT sur les points ; il ne se déduit pas d'un nom d'onglet.
 */
const auGrainPeriode = computed(
    () => serie.value?.x.some((point) => Array.isArray(point)) ?? false
)

/** Rien n'est calculé tant que le dépliant est fermé. */
const lignes = computed(() =>
    ouvert.value && serie.value
        ? lignesDuTableau({
              x: serie.value.x,
              v: serie.value.v,
              grain: grainReel.value,
              origines: serie.value.origines?.parPoint,
              n: serie.value.n,
              partiel: serie.value.partiel,
              attendu: serie.value.attendu,
              heuresAtteinte:
                  props.heuresAtteinteParSerie[
                      cleColonneAlignee(serie.value)
                  ] ?? null,
          })
        : []
)

/**
 * LES VALEURS DE L'INJECTION, par date (lot 5g). Elles se calculent exactement
 * comme celles du soutirage — même fonction, même grain — puis se rangent par
 * date pour être posées sur la ligne du même jour. Une date sans valeur
 * d'injection laisse la cellule VIDE : un tiret ou un 0 diraient « rien
 * injecté », ce que l'absence ne dit pas.
 */
const valeursInjectees = computed<Map<string, number | null>>(() => {
    const paire = paireMiroir.value
    if (!ouvert.value || !paire) return new Map()
    const lignesInjection = lignesDuTableau({
        x: paire.injection.x,
        v: paire.injection.v,
        grain: grainReel.value,
        origines: paire.injection.origines?.parPoint,
        n: paire.injection.n,
        partiel: paire.injection.partiel,
        attendu: paire.injection.attendu,
        heuresAtteinte: null,
    })
    return new Map(
        lignesInjection.map((ligne) => [
            ligne.x,
            props.sommable ? ligne.somme : ligne.moyenne,
        ])
    )
})

/** L'état du jour de la bande d'INJECTION, par date (lot 5g). */
const etatsParJourInjection = computed(
    () =>
        new Map<string, EtatCompletude>(
            props.joursDuContexteInjection.map((jour) => [jour.jour, jour.etat])
        )
)

/**
 * COMBIEN DE LIGNES, MÊME FERMÉ (V1). Le titre les annonce avant l'ouverture :
 * le compte se fait donc sans construire les lignes, en un seul passage sur les
 * horodates.
 */
const compteDeLignes = computed(() =>
    ouvert.value
        ? lignes.value.length
        : serie.value
          ? nombreDeLignes(serie.value.x, grainReel.value)
          : 0
)

const agrege = computed(() => grainReel.value !== props.grainServi)

/**
 * L'ORIGINE PAR LIGNE (décision de Mathieu du 11/09). La colonne n'apparaît que
 * si la réponse a dit d'où venaient les points : sans ce fait, une colonne vide
 * se lirait comme « on ne sait pas », alors qu'on n'a simplement pas demandé.
 */
const avecOrigine = computed(
    () => (serie.value?.origines?.pointsConnus ?? 0) > 0
)

/**
 * LA COUVERTURE PAR LIGNE (lot 3, E2). La colonne n'apparaît que si le lecteur a
 * compté les points : une colonne vide se lirait « aucun point », alors qu'elle
 * voudrait dire « on ne l'a pas demandé ».
 */
const avecPoints = computed(() =>
    lignes.value.some((ligne) => ligne.pointsAgreges !== null)
)

/**
 * L'ÉTAT DU JOUR, PAR DATE (V3). Une seule lecture de la bande, réutilisée par
 * toutes les lignes de toutes les pages.
 */
const etatsParJour = computed(
    () =>
        new Map<string, EtatCompletude>(
            props.joursDuContexte.map((jour) => [jour.jour, jour.etat])
        )
)

/**
 * La colonne « Qualité » n'apparaît que si la bande a des jours à dire. Sans
 * eux, une colonne vide se lirait « aucune qualité », alors qu'elle voudrait
 * dire « la bande n'a pas encore parlé ».
 */
const avecQualite = computed(() => etatsParJour.value.size > 0)

/** « Atteinte à » n'apparaît que si le lecteur date au moins un maximum. */
const avecAtteinte = computed(() =>
    lignes.value.some((ligne) => ligne.atteinteA !== null)
)

const colonnes = computed<ColonneValeurs[]>(() =>
    paireMiroir.value
        ? colonnesDuMiroir()
        : colonnesDuTableau({
              agrege: agrege.value,
              sommable: props.sommable,
              avecOrigine: avecOrigine.value,
              avecPoints: avecPoints.value,
              periode: auGrainPeriode.value,
              avecAtteinte: avecAtteinte.value,
              avecQualite: avecQualite.value,
          })
)

/**
 * LE TITRE, FERMÉ COMME OUVERT (V1) : le grain réel et le compte réel.
 * Le suffixe des statistiques suit les COLONNES réellement posées.
 */
const titre = computed(() =>
    titreDuDepliant({
        grain: grainReel.value,
        lignes: compteDeLignes.value,
        statistiques:
            agrege.value &&
            ["moyenne", "min", "max"].every((cle) =>
                colonnes.value.some((colonne) => colonne.cle === cle)
            ),
        periode: auGrainPeriode.value,
        minutesPasNatif: props.minutesPasNatif,
    })
)

const phrase = computed(() => phraseDuGrain(grainReel.value, props.grainServi))

/** Une colonne de nombres : alignée à droite, en chiffres à chasse fixe. */
function estNum(colonne: ColonneValeurs): boolean {
    return ![
        "x",
        "debut",
        "fin",
        "origine",
        "pointsAgreges",
        "qualite",
        "qualiteInjection",
        "atteinteA",
    ].includes(colonne.cle)
}

/**
 * LE MOT ET LA COULEUR DE LA QUALITÉ (V3) : ceux de la légende de la bande,
 * lus dans `ETATS_COMPLETUDE`. Aucune teinte nouvelle n'est introduite ici.
 */
function qualiteDeLaLigne(ligne: LigneValeurs) {
    const etat = etatsParJour.value.get(ligne.x.slice(0, 10))
    return etat ? ETATS_COMPLETUDE[etat] : null
}

/** La même chose, lue de la bande de l'INJECTION (lot 5g). */
function qualiteInjectionDeLaLigne(ligne: LigneValeurs) {
    const etat = etatsParJourInjection.value.get(ligne.x.slice(0, 10))
    return etat ? ETATS_COMPLETUDE[etat] : null
}

/** La valeur du sens regardé, telle que la colonne du miroir l'écrit. */
function valeurSoutiree(ligne: LigneValeurs): string {
    return props.sommable ? total(ligne) : cellule(ligne.moyenne)
}

/** La valeur injectée à cette date. Vide quand rien n'a été servi ce jour-là. */
function valeurInjectee(ligne: LigneValeurs): string {
    const valeur = valeursInjectees.value.get(ligne.x)
    return typeof valeur === "number" ? cellule(valeur) : ""
}

/** L'entête d'une colonne, avec son unité quand la colonne porte des nombres. */
function enTete(colonne: ColonneValeurs): string {
    return estNum(colonne) && props.unite
        ? `${colonne.libelle} (${props.unite})`
        : colonne.libelle
}

/** Le mot d'origine d'une ligne. Rien à écrire quand rien n'a été servi. */
function motOrigine(ligne: { origine: string | null }): string {
    return ligne.origine
        ? LIBELLES_ORIGINE[ligne.origine as keyof typeof LIBELLES_ORIGINE]
        : ""
}

/**
 * L'heure d'atteinte, telle qu'elle a été servie. `2026-07-03T13:42` n'a d'utile
 * que son heure : la date est déjà celle de la ligne.
 */
function heureAtteinte(ligne: LigneValeurs): string {
    if (!ligne.atteinteA) return ""
    const [, heure] = ligne.atteinteA.split("T")
    return (heure ?? ligne.atteinteA).slice(0, 5)
}

/** Ce qui s'écrit dans la colonne de couverture, vide quand rien ne la dit. */
function points(ligne: LigneValeurs): string {
    return texteDesPoints(ligne)
}

/** `null` = rien de mesuré : la cellule reste VIDE. */
function cellule(valeur: number | null): string {
    if (valeur === null) return ""
    // Le format vit dans `tableauValeurs.ts` : la carte du graphe chiffre les
    // mêmes séries en tête de son pied, et les deux doivent s'écrire pareil.
    return formateNombre(valeur)
}

/**
 * Le total d'une ligne, et ses pas manquants quand la fenêtre est trouée
 * (item 32). Le nombre se formate comme toute autre cellule.
 */
function total(ligne: LigneValeurs): string {
    return texteDuTotal(ligne, (valeur) => cellule(valeur))
}

/** `2026-09-08T13:00` → `08/09/2026 13:00` ; un jour seul reste un jour. */
function dateLisible(x: string): string {
    const [jour, heure] = x.split("T")
    const [a, m, j] = jour.split("-")
    return heure ? `${j}/${m}/${a} ${heure}` : `${j}/${m}/${a}`
}

function basculer(): void {
    ouvert.value = !ouvert.value
    if (ouvert.value) {
        emit("deplie", {
            grain: grainReel.value,
            lignes: lignes.value.length,
            serie: cleChoisie.value,
        })
    }
}

/** « Aller au » : la page de la date, ou rien du tout si elle n'y est pas. */
function allerA(): void {
    const cible = pageDeLaDate(lignes.value, dateCherchee.value, parPage.value)
    dateIntrouvable.value = cible === null
    if (cible !== null) premiere.value = cible * parPage.value
}

/**
 * Changer de série, de grain ou de taille de page ramène à la PREMIÈRE page :
 * la page 7 d'une série n'a aucun sens sur une autre, plus courte.
 */
watch([grainReel, cleChoisie, parPage], () => {
    premiere.value = 0
    dateIntrouvable.value = false
})

/** Le graphe change de séries : on retombe sur la première qu'il trace. */
watch(
    () => props.series.map((s) => s.cle).join("|"),
    () => {
        if (!props.series.some((s) => s.cle === cleChoisie.value)) {
            cleChoisie.value = props.series[0]?.cle ?? ""
        }
    }
)
</script>

<template>
    <section v-if="serie" class="flex flex-col gap-2" :data-valeurs="serie.cle">
        <!-- Le chevron est écrit DANS le contenu, pas passé en `icon` : le
             gabarit du bouton remplace icône et libellé par le contenu dès
             qu'il y en a un, si bien que l'icône passée en prop ne se dessinait
             pas et que le bouton se croyait « icône seule » — 36 px de large,
             libellé coupé en deux et rejeté hors de la carte. -->
        <BaseButton
            text
            size="small"
            :aria-expanded="ouvert"
            data-testid="bascule-valeurs"
            class="self-start px-1!"
            @click="basculer"
        >
            <i
                :class="ouvert ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                aria-hidden="true"
            />
            <!-- FERMÉ, LE BOUTON PORTE LE TITRE (V1, maquette v1.5 l.345) :
                 « Valeurs au jour, 92 lignes ». Le grain et le compte sont ce
                 qu'on veut savoir AVANT d'ouvrir ; les cacher jusqu'à
                 l'ouverture obligeait à ouvrir pour savoir s'il fallait ouvrir.
                 Ouvert, le bouton redevient l'action « Masquer les valeurs » et
                 le titre passe au `h4` du corps, qui dit exactement la même
                 chose. -->
            <span>{{ ouvert ? "Masquer les valeurs" : titre }}</span>
            <!-- Le nom de la série ne suit le bouton que s'il n'y en a
                 qu'une : avec plusieurs courbes, c'est le sélecteur qui dit
                 laquelle on regarde, et le répéter ici mentirait dès le
                 premier changement. -->
            <span
                v-if="!ouvert && !plusieurs"
                class="valeurs__bascule-serie font-normal text-color"
            >
                {{ serie.libelle }}
            </span>
        </BaseButton>

        <div
            v-if="ouvert"
            class="flex flex-col gap-3 rounded-md bg-surface-50 p-3"
            data-testid="corps-valeurs"
        >
            <!-- LE CHOIX DE LA SÉRIE, au-dessus du tableau qu'il commande.
                 Motif du sélecteur maison : libellé flottant posé, champ
                 dessous, lien `for`/`id`. -->
            <BaseFloatLabel
                v-if="plusieurs"
                variant="on"
                class="min-w-56 max-w-xs"
            >
                <BaseSelect
                    :id="idDuSelecteur"
                    v-model="cleChoisie"
                    class="w-full"
                    :options="choixDeSerie"
                    option-label="name"
                    option-value="value"
                    data-testid="choix-de-la-serie"
                />
                <label :for="idDuSelecteur">Série</label>
            </BaseFloatLabel>

            <header class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h4 class="m-0 text-sm font-semibold text-color">
                    {{ titre }}
                </h4>
                <span v-if="phrase" class="text-xs text-color">
                    {{ phrase }}
                </span>
                <span
                    v-for="mention in mentions"
                    :key="mention"
                    class="text-xs text-color"
                    data-testid="valeurs-plafonnee"
                >
                    {{ mention }}
                </span>
            </header>

            <div class="flex flex-wrap items-center gap-3">
                <label
                    :for="`aller-au-${serie.cle}`"
                    class="text-xs font-medium text-color"
                >
                    Aller au
                </label>
                <BaseInputText
                    :id="`aller-au-${serie.cle}`"
                    v-model="dateCherchee"
                    type="date"
                    size="small"
                    data-testid="aller-au"
                    @change="allerA"
                />
                <span
                    v-if="dateIntrouvable"
                    class="text-xs text-color"
                    data-testid="date-introuvable"
                >
                    Aucune ligne à cette date.
                </span>
            </div>

            <!-- Le défilement horizontal est INTERNE à la carte : la page ne
                 défile jamais de côté, même à 375 px (item 33). -->
            <div class="overflow-x-auto">
                <BaseDataTable
                    :value="lignes"
                    data-key="cle"
                    size="small"
                    striped-rows
                    paginator
                    :rows="parPage"
                    :rows-per-page-options="CHOIX_LIGNES_PAR_PAGE"
                    :first="premiere"
                    paginator-template="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                    current-page-report-template="Page {currentPage} sur {totalPages}"
                    :pt="{
                        table: 'valeurs__table min-w-[32rem]',
                        pcPaginator: { root: 'bg-transparent! text-xs' },
                    }"
                    @update:first="premiere = $event"
                    @update:rows="parPage = $event"
                >
                    <template #paginatorstart>
                        <span class="text-xs text-color">Lignes par page</span>
                    </template>

                    <BaseColumn
                        v-for="colonne in colonnes"
                        :key="colonne.cle"
                        :field="colonne.cle"
                        :header="enTete(colonne)"
                        :header-class="
                            estNum(colonne) ? 'valeurs__num text-right' : ''
                        "
                        :body-class="
                            estNum(colonne)
                                ? 'valeurs__num text-right font-mono tabular-nums'
                                : ''
                        "
                    >
                        <template #body="{ data }">
                            <template v-if="colonne.cle === 'x'">
                                {{ dateLisible(data.x) }}
                            </template>
                            <!-- LE DÉBUT ET LA FIN D'UNE PÉRIODE (V6) : « Du »
                                 et « Au », jamais une date seule qui ferait
                                 passer le début pour toute la période. -->
                            <template
                                v-else-if="
                                    colonne.cle === 'debut' ||
                                    colonne.cle === 'fin'
                                "
                            >
                                {{
                                    data[colonne.cle]
                                        ? dateLisible(data[colonne.cle])
                                        : ""
                                }}
                            </template>
                            <template v-else-if="colonne.cle === 'atteinteA'">
                                {{ heureAtteinte(data) }}
                            </template>
                            <!-- LA QUALITÉ DU JOUR (V3) : le mot et la couleur
                                 de la légende de la bande, relus tels quels.
                                 Une ligne sans mesure la porte aussi : c'est
                                 précisément là qu'elle dit quelque chose. -->
                            <template v-else-if="colonne.cle === 'qualite'">
                                <span
                                    v-if="qualiteDeLaLigne(data)"
                                    class="valeurs__qualite"
                                    :class="{
                                        'valeurs__qualite--hachures':
                                            qualiteDeLaLigne(data)!.motif ===
                                            'hachures',
                                    }"
                                    :data-qualite="qualiteDeLaLigne(data)!.cle"
                                >
                                    <span
                                        class="valeurs__qualite-puce"
                                        :style="{
                                            '--puce-fond':
                                                qualiteDeLaLigne(data)!.fond,
                                            '--puce-trait':
                                                qualiteDeLaLigne(data)!.trait,
                                        }"
                                        aria-hidden="true"
                                    ></span>
                                    {{ qualiteDeLaLigne(data)!.mot }}
                                </span>
                            </template>
                            <template
                                v-else-if="colonne.cle === 'pointsAgreges'"
                            >
                                {{ points(data) }}
                                <BaseTag
                                    v-if="data.partiel"
                                    severity="secondary"
                                    :value="MENTION_PARTIEL"
                                    data-testid="ligne-partielle"
                                    class="ml-1.5 text-[10px]"
                                />
                            </template>
                            <!-- L'ORIGINE PASSE À LA PASTILLE COMMUNE (V5,
                                 item 8 du brief lot 4). Avant, « Mesuré »
                                 s'écrivait en bleu ici et en vert dans la carte
                                 du graphe, à trente pixels de là : deux
                                 couleurs pour le même mot sur le même écran. -->
                            <template v-else-if="colonne.cle === 'origine'">
                                <DataOrigineBadge
                                    v-if="data.origine"
                                    :origine="data.origine"
                                    :libelle="motOrigine(data)"
                                />
                            </template>
                            <!-- LES DEUX COLONNES DU MIROIR (lot 5g) : deux
                                 nombres côte à côte, jamais leur différence. -->
                            <template v-else-if="colonne.cle === 'soutiree'">
                                {{ valeurSoutiree(data) }}
                            </template>
                            <template v-else-if="colonne.cle === 'injectee'">
                                {{ valeurInjectee(data) }}
                            </template>
                            <template
                                v-else-if="colonne.cle === 'qualiteInjection'"
                            >
                                <span
                                    v-if="qualiteInjectionDeLaLigne(data)"
                                    class="valeurs__qualite"
                                    :class="{
                                        'valeurs__qualite--hachures':
                                            qualiteInjectionDeLaLigne(data)!
                                                .motif === 'hachures',
                                    }"
                                    :data-qualite-injection="
                                        qualiteInjectionDeLaLigne(data)!.cle
                                    "
                                >
                                    <span
                                        class="valeurs__qualite-puce"
                                        :style="{
                                            '--puce-fond':
                                                qualiteInjectionDeLaLigne(data)!
                                                    .fond,
                                            '--puce-trait':
                                                qualiteInjectionDeLaLigne(data)!
                                                    .trait,
                                        }"
                                        aria-hidden="true"
                                    ></span>
                                    {{ qualiteInjectionDeLaLigne(data)!.mot }}
                                </span>
                            </template>
                            <template v-else-if="colonne.cle === 'somme'">
                                {{ total(data) }}
                            </template>
                            <template v-else>
                                {{
                                    cellule(data[colonne.cle] as number | null)
                                }}
                            </template>
                        </template>
                    </BaseColumn>
                </BaseDataTable>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* CE QUI RESTE DE CSS MAISON, et pourquoi.
   `.valeurs__num` et `.valeurs__bascule-serie` sont des SÉLECTEURS lus par les
   specs, pas des styles : l'alignement et la chasse fixe viennent des classes
   utilitaires posées à côté. `.valeurs__table th` reprend la casse et la taille
   de l'entête maison, que le préréglage PrimeVue ne pose pas.

   LA PASTILLE D'ORIGINE DU LOT 2 A ÉTÉ RETIRÉE (V5) : elle vit maintenant dans
   `DataOrigineBadge`, partagée avec la carte du graphe.

   La QUALITÉ (V3) ne peint aucune teinte à elle : sa puce lit `--puce-fond` et
   `--puce-trait`, posés depuis `ETATS_COMPLETUDE` — les mêmes variables et les
   mêmes valeurs que la légende de la bande. Le MOTIF hachuré porte l'absence
   sans dépendre de la seule couleur, et le mot reste au texte courant : la
   colonne se lit en noir et blanc. */
.valeurs__table :deep(th) {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.valeurs__qualite {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    color: var(--p-text-color, #2d2e32);
}

.valeurs__qualite-puce {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background-color: var(--puce-fond);
    border: 1px solid var(--puce-trait);
}

.valeurs__qualite--hachures .valeurs__qualite-puce {
    background-image: repeating-linear-gradient(
        45deg,
        var(--puce-trait) 0,
        var(--puce-trait) 1.5px,
        transparent 1.5px,
        transparent 4px
    );
}
</style>
